/**
 * Lo que se pega con Ctrl+V (⌘V en Mac) y es un archivo: una captura, una
 * imagen copiada, un archivo copiado del explorador. Funciones puras: las
 * prueba `paste.test.ts` sin navegador.
 */

/** Lo que importa del portapapeles. Un `DataTransfer` lo cumple. */
export interface ClipboardLike {
  readonly files?: ArrayLike<File> | null;
  readonly items?: ArrayLike<{
    readonly kind: string;
    getAsFile: () => File | null;
  }> | null;
}

/**
 * Los archivos del portapapeles, o ninguno si solo hay texto.
 *
 * Primero `files`, que es donde los deja el navegador; si viene vacío, los
 * `items` de tipo archivo, que es donde los dejan algunos (una imagen copiada
 * de una página, en Safari). Nunca los dos: el mismo archivo saldría dos
 * veces.
 */
export const filesOfClipboard = (
  data: ClipboardLike | null | undefined,
): File[] => {
  if (data === null || data === undefined) return [];
  const files = Array.from(data.files ?? []);
  if (files.length > 0) return files;
  return Array.from(data.items ?? [])
    .filter((item) => item.kind === "file")
    .map((item) => item.getAsFile())
    .filter((file): file is File => file !== null);
};

/** La extensión de lo que suele pegarse, por su tipo. */
const EXTENSIONS: Partial<Record<string, string>> = {
  "image/png": "png",
  "image/jpeg": "jpg",
  "image/gif": "gif",
  "image/webp": "webp",
  "image/bmp": "bmp",
  "image/svg+xml": "svg",
  "application/pdf": "pdf",
};

/**
 * Un nombre que no dice nada: vacío, o el `image.png` que ponen Chrome y
 * Firefox a toda captura pegada. Tres capturas llamadas igual no se
 * distinguen en el pedido.
 */
const UNNAMED = /^(image|imagen|blob)?(\.[a-z0-9]+)?$/i;

const pad = (value: number): string => String(value).padStart(2, "0");

/** `2026-10-08-142503`: se ordena solo y no lleva caracteres raros. */
const stampOf = (now: Date): string =>
  `${String(now.getFullYear())}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}-` +
  `${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;

/**
 * El nombre con que se adjunta lo pegado: el suyo si lo tiene, y si no,
 * `pegado-<fecha>.png`. `index` separa los que se pegan a la vez.
 */
export const pastedName = (file: File, now: Date, index = 0): string => {
  if (!UNNAMED.test(file.name)) return file.name;
  const extension =
    EXTENSIONS[file.type] ?? /\.([a-z0-9]+)$/i.exec(file.name)?.[1] ?? "png";
  const suffix = index === 0 ? "" : `-${String(index + 1)}`;
  return `pegado-${stampOf(now)}${suffix}.${extension}`;
};

/** Lo pegado, con su nombre: el mismo archivo si ya tenía uno. */
export const namedPaste = (files: readonly File[], now: Date): File[] =>
  files.map((file, index) => {
    const name = pastedName(file, now, index);
    return name === file.name
      ? file
      : new File([file], name, {
          type: file.type,
          lastModified: file.lastModified,
        });
  });
